//PrivacyPolicyModal.js
import React, { useState } from 'react';
import { X, Shield, ChevronDown, ChevronUp, Lock, Eye, Users, Database, Settings, FileText } from 'lucide-react';
import Button from '../../Button/Button';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      // If the section is already open, close it
      if (prev[section]) {
        return {
          ...prev,
          [section]: false
        };
      }
      
      // Otherwise, close all sections and open only the clicked one
      const newState = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });
      newState[section] = true;
      
      return newState;
    });
  };

  // Check if any section is expanded to determine scroll behavior
  const hasExpandedSections = Object.values(expandedSections).some(Boolean);

  const CollapsibleSection = ({ title, children, sectionKey, icon: Icon }) => (
    <div className="mb-3 group">
      <button
        onClick={() => toggleSection(sectionKey)}
        className="flex items-center justify-between w-full text-left p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 border border-gray-200 hover:border-blue-200 shadow-sm hover:shadow-md"
      >
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-white shadow-sm group-hover:bg-blue-100 transition-colors duration-300">
            <Icon className="w-4 h-4 text-blue-600" />
          </div>
          <h3 className="text-base font-semibold text-gray-800 group-hover:text-blue-700 transition-colors duration-300">{title}</h3>
        </div>
        <div className="flex items-center">
          {expandedSections[sectionKey] ? (
            <ChevronUp className="w-4 h-4 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
          )}
        </div>
      </button>
      {expandedSections[sectionKey] && (
        <div className="mt-2 mx-2 p-4 bg-white border border-gray-100 rounded-lg shadow-sm">
          {children}
        </div>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-2xl overflow-hidden flex flex-col shadow-2xl"
        style={{
          width: '90%',
          maxWidth: '800px',
          height: '85vh',
          maxHeight: '85vh'
        }}
      >
        {/* Header */}
        <div className="relative p-6 bg-gradient-to-r from-blue-600 to-indigo-700 text-gray-800">
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                <Shield className="w-6 h-6 text-gray-800" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Privacy Policy</h2>
                <p className="text-blue-100 text-sm">Your data protection and privacy rights</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="small"
              icon={<X className="w-4 h-4" />}
              onClick={onClose}
              className="text-gray-800 hover:bg-white/20 hover:text-gray-100 bg-transparent border-none"
            />
          </div>
        </div>

        {/* Content */}
        <div className={`flex-1 ${hasExpandedSections ? 'overflow-y-auto' : 'overflow-hidden'}`}>
          <div className="p-6">
            {/* Intro Section */}
            <div className="text-center mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
              <div className="text-xs text-gray-600 mb-3 flex items-center justify-center gap-4">
                <span className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <strong>Effective:</strong> January 1, 2025
                </span>
                <span className="flex items-center gap-1">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  <strong>Updated:</strong> January 1, 2025
                </span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                At <span className="font-semibold text-blue-600">fdbk</span>, we are committed to protecting your privacy and ensuring the security of your data. 
                This Privacy Policy explains how we collect, use, and protect information when you use our 
                feedback kiosk platform and services.
              </p>
            </div>

            <div className="space-y-3">
              <CollapsibleSection title="Information We Collect" sectionKey="collect" icon={Database}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 flex items-center gap-2 text-sm">
                      <Users className="w-4 h-4" />
                      Customer Feedback Data
                    </h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Feedback ratings and responses collected through kiosks</li>
                      <li>Follow-up question responses</li>
                      <li>Timestamp and location data of feedback submissions</li>
                      <li>Optional demographic information if provided</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold mb-2 text-indigo-800 flex items-center gap-2 text-sm">
                      <Settings className="w-4 h-4" />
                      Business Account Information
                    </h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Business name, contact information, and billing details</li>
                      <li>User account credentials and preferences</li>
                      <li>Kiosk configuration and custom questions</li>
                      <li>Usage analytics and platform interaction data</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                    <h4 className="font-semibold mb-2 text-slate-800 flex items-center gap-2 text-sm">
                      <FileText className="w-4 h-4" />
                      Technical Data
                    </h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Device information and kiosk performance metrics</li>
                      <li>IP addresses and network information</li>
                      <li>Browser type and version</li>
                      <li>System logs and error reports</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="How We Use Your Information" sectionKey="use" icon={Settings}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 text-sm">Service Delivery</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Process and analyze customer feedback</li>
                      <li>Generate reports and analytics dashboards</li>
                      <li>Provide dynamic follow-up questions</li>
                      <li>Maintain and improve kiosk functionality</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold mb-2 text-indigo-800 text-sm">Customer Support</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Respond to support requests and technical issues</li>
                      <li>Provide training and onboarding assistance</li>
                      <li>Send important service updates and notifications</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                    <h4 className="font-semibold mb-2 text-slate-800 text-sm">Legal and Security</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Comply with legal obligations and regulations</li>
                      <li>Protect against fraud and unauthorized access</li>
                      <li>Enforce our Terms of Service</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Data Sharing and Disclosure" sectionKey="sharing" icon={Users}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-gray-800 rounded-lg shadow-lg">
                    <p className="font-bold text-sm flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      We do not sell your data to third parties.
                    </p>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3 text-gray-800 text-sm">Limited Sharing Scenarios</h4>
                    <div className="space-y-3">
                      <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                        <div className="text-sm">
                          <strong className="text-blue-800">Service Providers:</strong>
                          <span className="text-gray-700 ml-1">Trusted vendors who help us deliver our services (cloud hosting, payment processing)</span>
                        </div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                        <div className="text-sm">
                          <strong className="text-indigo-800">Legal Requirements:</strong>
                          <span className="text-gray-700 ml-1">When required by law, court order, or government request</span>
                        </div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                        <div className="text-sm">
                          <strong className="text-slate-800">Business Transfers:</strong>
                          <span className="text-gray-700 ml-1">In the event of a merger, acquisition, or sale of assets</span>
                        </div>
                      </div>
                      <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                        <div className="text-sm">
                          <strong className="text-purple-800">Google Reviews Integration:</strong>
                          <span className="text-gray-700 ml-1">Only with explicit consent for review management features</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Data Security and Retention" sectionKey="security" icon={Lock}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <h4 className="font-semibold mb-2 text-blue-800 text-sm">Security Measures</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Industry-standard encryption for data in transit and at rest</li>
                      <li>Regular security audits and vulnerability assessments</li>
                      <li>Access controls and employee training programs</li>
                      <li>Secure data centers with 24/7 monitoring</li>
                    </ul>
                  </div>

                  <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                    <h4 className="font-semibold mb-2 text-indigo-800 text-sm">Data Retention</h4>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Feedback data: Retained for the duration of your subscription plus 90 days</li>
                      <li>Account information: Retained until account deletion</li>
                      <li>System logs: Retained for 1 year for security purposes</li>
                      <li>Billing records: Retained for 7 years as required by law</li>
                    </ul>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Your Rights and Choices" sectionKey="rights" icon={Eye}>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                      <h5 className="font-semibold text-blue-800 mb-1 text-sm">Access</h5>
                      <p className="text-xs text-gray-700">Request copies of your data</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                      <h5 className="font-semibold text-slate-800 mb-1 text-sm">Deletion</h5>
                      <p className="text-xs text-gray-700">Request deletion of your data</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200">
                      <h5 className="font-semibold text-indigo-800 mb-1 text-sm">Portability</h5>
                      <p className="text-xs text-gray-700">Export your data in common formats</p>
                    </div>
                    <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                      <h5 className="font-semibold text-purple-800 mb-1 text-sm">Opt-out</h5>
                      <p className="text-xs text-gray-700">Unsubscribe from marketing communications</p>
                    </div>
                  </div>

                  <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg text-gray-800">
                    <p className="text-sm font-medium flex items-center gap-2">
                      <div className="p-1 bg-white/20 rounded">
                        <Shield className="w-3 h-3" />
                      </div>
                      To exercise these rights, contact us at: <strong>privacy@fdbkinsights.com</strong>
                    </p>
                  </div>
                </div>
              </CollapsibleSection>

              <CollapsibleSection title="Cookies and Tracking" sectionKey="cookies" icon={Eye}>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                    <p className="mb-3 text-gray-700 font-medium text-sm">We use cookies and similar technologies to:</p>
                    <ul className="list-disc pl-4 space-y-1 text-gray-700 text-xs">
                      <li>Maintain your login session</li>
                      <li>Remember your preferences and settings</li>
                      <li>Analyze platform usage and performance</li>
                      <li>Provide personalized experiences</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200">
                    <p className="text-gray-700 text-sm">
                      You can control cookie preferences through your browser settings.
                    </p>
                  </div>
                </div>
              </CollapsibleSection>
            </div>

            {/* Contact Section */}
            <div className="mt-6 p-4 bg-gradient-to-r from-gray-800 to-gray-900 rounded-lg text-gray-800">
              <h3 className="font-bold text-base mb-4 flex items-center gap-2">
                <div className="p-1 bg-white/20 rounded">
                  <Shield className="w-4 h-4" />
                </div>
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="font-semibold text-blue-300 mb-1 text-xs">Data Protection Officer</p>
                  <p className="text-gray-300 text-xs">privacy@fdbkinsights.com</p>
                </div>
                <div>
                  <p className="font-semibold text-green-300 mb-1 text-xs">General Inquiries</p>
                  <p className="text-gray-300 text-xs">development@fdbkinsights.com</p>
                </div>
                {/* <div>
                  <p className="font-semibold text-purple-300 mb-1 text-xs">Business Address</p>
                  <p className="text-gray-300 text-xs">[Your Business Address]</p>
                </div> */}
              </div>
            </div>

          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicyModal;